import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  isAnySurfaceInboundEvent,
  isSurfaceEvent,
  isLegacyStoriesEvent,
  type SurfaceContract,
  type SurfaceEvent,
  type SurfaceMode,
} from "types/surfaces";
import {
  resolveNextEmbedPath,
  type BridgeLocale,
} from "i18n/resolveNextEmbedPath";

/**
 * Props for `<LegacySurfaceBridge>`. The bridge is intentionally dumb:
 * it does not own presentation (that's `SurfaceHost`), nor does it
 * care about the parent entity's cache keys (that's
 * `useSurfaceMutationListener`). Its only job is to:
 *
 *   - Build the correct same-origin Next embed URL from the contract.
 *   - Render the transport (iframe today; stubbed for client/route).
 *   - Listen for inbound `SurfaceEvent`s and call the typed callbacks.
 *   - Filter cross-origin / malformed messages so consumers never
 *     touch a raw `MessageEvent`.
 */
export interface LegacySurfaceBridgeProps<TInitial = unknown>
  extends SurfaceContract<TInitial> {
  /**
   * Transport selection. Only `"iframe"` is implemented in this pass.
   * `"client"` and `"route"` are declared so consumers can opt in
   * once the shared-package or full route-owned variants ship —
   * passing them today throws a controlled runtime error instead of
   * silently mis-rendering (rule 24).
   */
  mode: SurfaceMode;
  /** Locale override. Omit to use `navigator.language`. */
  locale?: BridgeLocale;
  /**
   * Optional accessible name for the iframe. Defaults to
   * `${surfaceKind}-bridge`. Prefer passing a localized string.
   */
  title?: string;
  /**
   * Optional extra query params forwarded to the embed URL. `embed=1`
   * is always set; do not pass it here.
   */
  extraQuery?: Record<string, string | number | undefined>;
  /** Called when the body posts `surface:close`. */
  onClose?: () => void;
  /** Called when the body posts `surface:navigate`. */
  onNavigate?: (payload: { pathname: string; replace?: boolean }) => void;
  /** Called when the body posts `surface:mutation`. */
  onMutation?: (payload: {
    entityType: string;
    entityId: string | number;
    kind: "create" | "update" | "delete";
    childEntityType?: string;
    childEntityId?: string | number;
  }) => void;
  /** Called when the composer inside the body toggles dirty state. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Called when the body reports intrinsic height (inline mode). */
  onHeightChange?: (px: number) => void;
  /** Called when the body finishes initial mount + data load. */
  onReady?: () => void;
  /**
   * Fallback UI while the flag is still being resolved or the
   * embed URL cannot be built. Defaults to `null` (render nothing).
   */
  fallback?: React.ReactNode;
  /**
   * Optional className applied to the transport root (iframe for iframe
   * mode). Useful for sizing constraints when inside a drawer.
   */
  className?: string;
  /**
   * Test hook forwarded to the iframe root. Stable across renders so
   * RTL/Playwright can select it.
   */
  testId?: string;
}

/**
 * Build the embed URL from contract props. Extracted so iframe-mode
 * branches and future client-mode branches share the same URL logic
 * for analytics consistency.
 */
const useResolvedEmbedUrl = <T,>(
  props: LegacySurfaceBridgeProps<T>
): string => {
  return useMemo(() => {
    const originPath = props.origin.route;
    // `origin.route` is informational; we send it along as a query
    // param so the embed body can tag analytics with the source
    // page without guessing from `document.referrer`.
    const mergedQuery: Record<string, string | number | undefined> = {
      origin_route: originPath,
      ...(props.extraQuery ?? {}),
    };
    return resolveNextEmbedPath({
      surfaceKind: props.surfaceKind,
      entityType: props.entityType,
      entityId: props.entityId,
      locale: props.locale,
      query: mergedQuery,
      withEmbed: true,
    });
  }, [
    props.entityId,
    props.entityType,
    props.extraQuery,
    props.locale,
    props.origin.route,
    props.surfaceKind,
  ]);
};

/**
 * `<LegacySurfaceBridge>` — CRA-side transport that renders a same-origin
 * Next.js embed. Currently iframe-only; the `mode` prop is the
 * migration door: future surfaces can set `mode="client"` to mount
 * the body natively once the shared React package exists.
 *
 * Always renders inside a `<SurfaceHost>`; do not mount it directly
 * into a route.
 */
export function LegacySurfaceBridge<TInitial = unknown>(
  props: LegacySurfaceBridgeProps<TInitial>
): React.JSX.Element | null {
  const {
    mode,
    surfaceKind,
    onClose,
    onNavigate,
    onMutation,
    onDirtyChange,
    onHeightChange,
    onReady,
    title,
    fallback = null,
    className,
    testId,
  } = props;

  const embedUrl = useResolvedEmbedUrl(props);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleEvent = useCallback(
    (event: SurfaceEvent): void => {
      switch (event.type) {
        case "surface:close":
          onClose?.();
          return;
        case "surface:navigate":
          onNavigate?.({
            pathname: event.pathname,
            replace: event.replace,
          });
          return;
        case "surface:mutation":
          onMutation?.({
            entityType: event.entityType,
            entityId: event.entityId,
            kind: event.kind,
            childEntityType: event.childEntityType,
            childEntityId: event.childEntityId,
          });
          return;
        case "surface:dirty":
          onDirtyChange?.(event.dirty);
          return;
        case "surface:height":
          onHeightChange?.(event.px);
          return;
        case "surface:ready":
          onReady?.();
          return;
        default:
          return;
      }
    },
    [
      onClose,
      onDirtyChange,
      onHeightChange,
      onMutation,
      onNavigate,
      onReady,
    ]
  );

  useEffect(() => {
    if (mode !== "iframe" || typeof window === "undefined") {
      return;
    }
    const listener = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (!isAnySurfaceInboundEvent(event.data)) {
        return;
      }
      // Back-compat: translate legacy stories vocabulary into the
      // standardized events so consumers only write one switch.
      if (isLegacyStoriesEvent(event.data)) {
        if (event.data.type === "stories:close") {
          handleEvent({ type: "surface:close" });
        }
        // `stories:advance` is purely informational; no-op translation.
        return;
      }
      if (isSurfaceEvent(event.data)) {
        handleEvent(event.data);
      }
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [handleEvent, mode]);

  if (!embedUrl) {
    return <>{fallback}</>;
  }

  if (mode === "iframe") {
    return (
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={title ?? `${surfaceKind}-bridge`}
        className={className ?? "h-full w-full border-0"}
        allow="clipboard-write"
        referrerPolicy="same-origin"
        data-testid={testId}
      />
    );
  }

  if (mode === "client") {
    throw new Error(
      "LegacySurfaceBridge: mode='client' is declared in the contract but not yet implemented. Track the shared-surface-package migration in ADR 011 before enabling."
    );
  }

  if (mode === "route") {
    throw new Error(
      "LegacySurfaceBridge: mode='route' is declared in the contract but not yet implemented. When a surface graduates to Next-owned routing, navigate with react-router-dom directly instead of mounting this bridge."
    );
  }

  return <>{fallback}</>;
}

export default LegacySurfaceBridge;
